import React from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { DashboardToolCardGrid } from 'apps/sigma2-ui/src/components/overview/DashboardToolCardGrid';
import { DashboardToolCardPropsWithoutVariant } from 'apps/sigma2-ui/src/components/overview/DashboardToolCardGrid';
import { CopilotIconSvg } from 'apps/sigma2-ui/src/components/svg/CopilotIcon';
import { IconExploreSvg } from 'apps/sigma2-ui/src/components/SideNavigationMenu';
import { IconExploreTableViewSvg } from 'apps/sigma2-ui/src/components/SideNavigationMenu';
import { ProgressIconSvg } from 'apps/sigma2-ui/src/components/SideNavigationMenu';
import { BookmarkOutlineIconSvg } from 'apps/sigma2-ui/src/components/svg/BookmarkOutlineIcon';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { KeyFactorsIconSvg } from 'apps/sigma2-ui/src/components/svg/KeyFactorsIcon';

export interface DashboardNoIntelligenceSummaryProps {
  /**
   * Whether the hiring copilot is enabled.
   */
  hiringCopilotEnabled: boolean;

  /**
   * Whether the promotion copilot is enabled.
   */
  promotionCopilotEnabled: boolean;

  /**
   * Whether pay benchmarking is enabled.
   */
  payBenchmarkingEnabled: boolean;

  /**
   * Whether pay correction is enabled.
   */
  payCorrectionEnabled: boolean;

  /**
   * Whether the key factors is enabled.
   */
  keyFactorsEnabled: boolean;

  /**
   * Whether the heatmap is enabled.
   */
  heatmapEnabled: boolean;

  /**
   * Whether the dashboards are enabled.
   */
  dashboardsEnabled: boolean;

  /**
   * Whether the saved analyses are enabled.
   */
  savedAnalysesEnabled: boolean;

  /**
   * Whether the progress page is enabled.
   */
  progressEnabled: boolean;

  /**
   * Whether the table view is enabled.
   */
  tableViewEnabled: boolean;
}

export const getDashboardNoIntelligenceAllCards = ({
  language,
}: {
  language: Phrases;
}) => {
  const KEY_FACTORS_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardSuccessPhenotypeBody(),
    headerTranslatedContent: language.keyFactorsPageTitle(),
    largeIconSrc: '/images/overview/overview-card-success-factors.png',
    glowColor: '#BED8F5',
    gradientColor: '#007AFF',
    smallIcon: KeyFactorsIconSvg,
    href: '/success-factors',
  };

  const HEATMAP_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardHeatmapBody(),
    headerTranslatedContent: language.explorePageTitle(),
    largeIconSrc: '/images/overview/overview-card-heatmap.png',
    glowColor: '#FB9136',
    gradientColor: '#EC6407',
    smallIcon: IconExploreSvg,
    href: '/explore',
  };

  const DASHBOARDS_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardDashboardsBody(),
    headerTranslatedContent: language.dashboards(),
    largeIconSrc: '/images/overview/overview-card-dashboards.png',
    glowColor: '#1AAEE0',
    gradientColor: '#0083B1',
    smallIcon: BookmarkOutlineIconSvg,
    href: '/dashboards',
  };

  const SAVED_ANALYSES_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardSavedAnalysesBody(),
    headerTranslatedContent: language.savedAnalyses(),
    largeIconSrc: '/images/overview/overview-card-saved-analyses.png',
    glowColor: '#475183',
    gradientColor: '#7A87C9',
    smallIcon: BookmarkOutlineIconSvg,
    href: '/saved-analyses',
  };

  const PROGRESS_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardProgressBody(),
    headerTranslatedContent: language.progress(),
    largeIconSrc: '/images/overview/overview-card-progress.png',
    glowColor: '#3232A1',
    gradientColor: '#1D1DC3',
    smallIcon: ProgressIconSvg,
    href: '/progress',
  };

  const HIRING_COPILOT_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardHiringCopilotBody(),
    headerTranslatedContent: language.hiringCopilotTitle(),
    largeIconSrc: '/images/overview/overview-card-hiring-copilot.png',
    glowColor: '#E1D2F5',
    gradientColor: '#9747FF',
    smallIcon: CopilotIconSvg,
    href: '/copilot/hiring',
  };

  const PAY_BENCHMARKING_COPILOT_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardPayBenchmarkingCopilotBody(),
    headerTranslatedContent: language.payBenchmarkingTitle(),
    largeIconSrc: '/images/overview/overview-card-pay-benchmarking.png',
    glowColor: '#efebdc',
    gradientColor: '#F9AC16',
    smallIcon: CopilotIconSvg,
    href: '/copilot/pay-benchmarking',
  };

  const PROMOTION_COPILOT_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardPromotionCopilotBody(),
    headerTranslatedContent: language.promotionCopilotTitle(),
    largeIconSrc: '/images/overview/overview-card-promotion-copilot.png',
    glowColor: '#b5eefa',
    gradientColor: '#1AAEE0',
    smallIcon: CopilotIconSvg,
    href: '/copilot/promotion',
  };

  const PAY_CORRECTION_COPILOT_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardPayCorrectionCopilotBody(),
    headerTranslatedContent: language.payCorrectionTitle(),
    largeIconSrc: '/images/overview/overview-card-comp-copilot.png',
    glowColor: '#d1fbf8',
    gradientColor: '#12e9d5',
    smallIcon: CopilotIconSvg,
    href: '/copilot/pay-correction',
  };

  const TABLE_VIEW_CARD: DashboardToolCardPropsWithoutVariant = {
    bodyTranslatedContent: language.overviewCardTableViewBody(),
    headerTranslatedContent: language.overviewCardTableViewTitle(),
    largeIconSrc: '/images/overview/overview-card-table-view.png',
    glowColor: '#1AAEE0',
    gradientColor: '#509AD2',
    smallIcon: IconExploreTableViewSvg,
    href: '/explore/table-view',
  };

  return {
    KEY_FACTORS_CARD,
    HEATMAP_CARD,
    DASHBOARDS_CARD,
    SAVED_ANALYSES_CARD,
    PROGRESS_CARD,
    HIRING_COPILOT_CARD,
    PAY_BENCHMARKING_COPILOT_CARD,
    PROMOTION_COPILOT_CARD,
    PAY_CORRECTION_COPILOT_CARD,
    TABLE_VIEW_CARD,
  };
};
// eslint-disable-next-line complexity
export const DashboardNoIntelligenceSummary = ({
  hiringCopilotEnabled,
  promotionCopilotEnabled,
  payCorrectionEnabled,
  payBenchmarkingEnabled,
  keyFactorsEnabled,
  heatmapEnabled,
  dashboardsEnabled,
  savedAnalysesEnabled,
  progressEnabled,
  tableViewEnabled,
}: DashboardNoIntelligenceSummaryProps) => {
  const language = useLanguage();
  const {
    HEATMAP_CARD,
    DASHBOARDS_CARD,
    SAVED_ANALYSES_CARD,
    KEY_FACTORS_CARD,
    PROGRESS_CARD,
    HIRING_COPILOT_CARD,
    PAY_BENCHMARKING_COPILOT_CARD,
    PROMOTION_COPILOT_CARD,
    PAY_CORRECTION_COPILOT_CARD,
    TABLE_VIEW_CARD,
  } = getDashboardNoIntelligenceAllCards({ language });

  return (
    <Spacing direction={'vertical'} size={'md'}>
      <DashboardToolCardGrid
        cards={[
          ...(keyFactorsEnabled ? [KEY_FACTORS_CARD] : []),
          ...(heatmapEnabled ? [HEATMAP_CARD] : []),
          ...(dashboardsEnabled ? [DASHBOARDS_CARD] : []),
          ...(savedAnalysesEnabled ? [SAVED_ANALYSES_CARD] : []),
          ...(progressEnabled ? [PROGRESS_CARD] : []),
          // TODO: Someone would be very generous to rework this code to use the CopilotOrder.
          ...(hiringCopilotEnabled ? [HIRING_COPILOT_CARD] : []),
          ...(payBenchmarkingEnabled ? [PAY_BENCHMARKING_COPILOT_CARD] : []),
          ...(payCorrectionEnabled ? [PAY_CORRECTION_COPILOT_CARD] : []),
          ...(promotionCopilotEnabled ? [PROMOTION_COPILOT_CARD] : []),
          ...(tableViewEnabled ? [TABLE_VIEW_CARD] : []),
        ]}
      />
    </Spacing>
  );
};
